import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@/apollo';
import { X, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { GET_SERVER_HEALTH_STATUS } from '../../graphql/system-health';

interface ServerRestartModalProps {
  isOpen: boolean;
  onClose: () => void;
  restartInitiated: boolean;
}

export const ServerRestartModal: React.FC<ServerRestartModalProps> = ({
  isOpen,
  onClose,
  restartInitiated
}) => {
  const [stage, setStage] = useState<'waiting' | 'checking' | 'healthy' | 'failed'>('waiting');
  const [checkCount, setCheckCount] = useState(0);
  const [timeWaited, setTimeWaited] = useState(0);

  const [checkHealth, { data: healthData }] = useLazyQuery(
    GET_SERVER_HEALTH_STATUS,
    {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache'
    }
  );

  useEffect(() => {
    if (!isOpen || !restartInitiated) {
      setStage('waiting');
      setCheckCount(0);
      setTimeWaited(0);
      return;
    }

    // Wait 5 seconds before starting health checks (give server time to restart)
    const initialWaitTimer = setTimeout(() => {
      setStage('checking');
      startHealthChecks();
    }, 5000);

    // Update time counter
    const timeTimer = setInterval(() => {
      setTimeWaited(prev => prev + 1);
    }, 1000);

    return () => {
      clearTimeout(initialWaitTimer);
      clearInterval(timeTimer);
    };
  }, [isOpen, restartInitiated]);

  const startHealthChecks = () => {
    const healthCheckInterval = setInterval(async () => {
      try {
        setCheckCount(prev => prev + 1);
        await checkHealth();
        
        // Check if server is healthy
        if (healthData?.serverHealthStatus?.healthy) {
          setStage('healthy');
          clearInterval(healthCheckInterval);
          
          // Auto-close modal after 2 seconds
          setTimeout(() => {
            onClose();
          }, 2000);
          return;
        }
        
        // If we've been checking for more than 60 seconds, consider it failed
        if (checkCount > 12) { // 12 checks * 5 seconds = 60 seconds
          setStage('failed');
          clearInterval(healthCheckInterval);
        }
      } catch (error) {
        console.log('Health check failed (expected during restart):', error);
        // During restart, health checks are expected to fail
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(healthCheckInterval);
  };

  const getStageContent = () => {
    switch (stage) {
      case 'waiting':
        return {
          icon: <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#005a9b' }} />,
          title: 'Restarting Server',
          message: 'Please wait while the server restarts...',
          description: 'This process may take a few moments.',
          color: '#005a9b'
        };
      
      case 'checking':
        return {
          icon: <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#ffc107' }} />,
          title: 'Checking Server Health',
          message: `Health check ${checkCount}/12 - Server reconnecting...`,
          description: 'Testing server connectivity and database status.',
          color: '#ffc107'
        };
      
      case 'healthy':
        return {
          icon: <CheckCircle className="w-8 h-8" style={{ color: '#28a745' }} />,
          title: 'Server Restart Complete',
          message: 'Server is healthy and responding normally.',
          description: 'All services are operational. This dialog will close automatically.',
          color: '#28a745'
        };
      
      case 'failed':
        return {
          icon: <AlertCircle className="w-8 h-8" style={{ color: '#a4262c' }} />,
          title: 'Server Health Check Failed',
          message: 'Unable to confirm server is healthy after restart.',
          description: 'Please check server logs and try refreshing the page.',
          color: '#a4262c'
        };
      
      default:
        return {
          icon: <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#005a9b' }} />,
          title: 'Processing',
          message: 'Please wait...',
          description: '',
          color: '#005a9b'
        };
    }
  };

  if (!isOpen) return null;

  const content = getStageContent();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div 
        className="rounded-xl shadow-xl max-w-md w-full mx-4"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-xl)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            System Maintenance
          </h3>
          {stage === 'healthy' || stage === 'failed' ? (
            <button
              onClick={onClose}
              className="p-1 rounded-lg transition-colors"
              style={{ color: 'var(--color-text-muted)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <X className="w-5 h-5" />
            </button>
          ) : null}
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex flex-col items-center text-center space-y-4">
            {content.icon}
            
            <div className="space-y-2">
              <h4 className="text-xl font-medium" style={{ color: content.color }}>
                {content.title}
              </h4>
              <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                {content.message}
              </p>
              {content.description && (
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {content.description}
                </p>
              )}
            </div>

            {/* Progress indicator */}
            {stage === 'waiting' || stage === 'checking' ? (
              <div className="w-full">
                <div className="flex justify-between text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  <span>Time elapsed: {timeWaited}s</span>
                  {stage === 'checking' && (
                    <span>{checkCount}/12 checks</span>
                  )}
                </div>
                <div 
                  className="w-full rounded-full h-2"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                >
                  <div 
                    className="h-2 rounded-full transition-all duration-1000"
                    style={{ 
                      width: stage === 'waiting' 
                        ? `${Math.min((timeWaited / 5) * 100, 100)}%`
                        : `${Math.min((checkCount / 12) * 100, 100)}%`,
                      backgroundColor: content.color
                    }}
                  ></div>
                </div>
              </div>
            ) : null}

            {/* Action buttons */}
            {stage === 'failed' && (
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setStage('checking');
                    setCheckCount(0);
                    startHealthChecks();
                  }}
                  className="px-4 py-2 text-white rounded-lg transition-colors"
                  style={{ backgroundColor: 'var(--color-accent)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-accent)';
                  }}
                >
                  Retry Health Check
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 border rounded-lg transition-colors"
                  style={{ 
                    borderColor: 'var(--color-border)', 
                    color: 'var(--color-text-primary)',
                    backgroundColor: 'var(--color-bg-secondary)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
